import dayjs from 'dayjs';
import { buildActaInterviewedData } from '../utils/actaPayload';
import { FallaVisita } from './visitasTypes';
import { VisitaEstado } from './visitasStatus';

// Tipo de evento del log de auditoría con el que el backend registra cada falla
// de la visita: el acta de visita fallida se pide con el id de ese evento.
export const TIPO_EVENTO_FALLA = 'VISITA_MARCADA_FALLIDA';

// Fallas que trae la grilla (GET /visitas). Las visitas que fallaron antes de
// que el backend registrara el evento de auditoría llegan sin ellas.
export const fallasDeLaVisita = (visita: any): FallaVisita[] =>
    Array.isArray(visita?.fallas) ? visita.fallas : [];

export const esVisitaFallida = (visita: any): boolean => visita?.estado === VisitaEstado.FALLIDA;

// El acta de visita fallida se ofrece si la visita falló alguna vez: o la
// grilla trae sus fallas (puede haberse retomado y hasta realizado después), o
// la visita quedó en estado Fallida — en ese caso la falla se resuelve del
// historial de auditoría recién al descargar.
export const puedeDescargarActaFallida = (visita: any, perfilHabilitado?: boolean): boolean =>
    Boolean(perfilHabilitado) && (fallasDeLaVisita(visita).length > 0 || esVisitaFallida(visita));

// Fallas del historial de auditoría (GET /visitas/{id}/log-auditoria) con el
// mismo contrato que las de la grilla. El historial viene de la más nueva a la
// más vieja; se ordenan como en la grilla para que el drawer de descarga no
// cambie de orden según de dónde salieron.
export const fallasDesdeLogAuditoria = (eventos: any): FallaVisita[] =>
    (Array.isArray(eventos) ? eventos : [])
        .filter((evento: any) => evento?.tipoEvento === TIPO_EVENTO_FALLA)
        .map((evento: any) => ({
            idEvento: evento.id,
            fecha: evento.fecha,
            motivoCodigo: evento?.detalle?.motivoCodigo ?? null,
            motivoDescripcion: evento?.detalle?.motivoDescripcion ?? null
        }))
        .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

// El drawer de descarga lista primero la falla más reciente.
export const ordenarFallasRecienteAntigua = (fallas: FallaVisita[]): FallaVisita[] =>
    [...fallas].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

// Fecha y hora de la falla como se muestran en el drawer y en los avisos.
export const fechaDeFalla = (falla: FallaVisita): string =>
    dayjs(falla.fecha).format('DD/MM/YYYY HH:mm');

// Cada falla tiene su acta: la fecha y hora en el nombre distinguen los
// archivos cuando se bajan varias juntas.
export const nombreArchivoActaFallida = (falla: FallaVisita, prefijo: string): string =>
    `${prefijo}_${dayjs(falla.fecha).format('DD-MM-YYYY_HHmm')}.pdf`;

export const idDeLaVisita = (visita: any): number => visita?.idVisita || visita?.id;

// "<empresa> — <establecimiento>" del encabezado del drawer. La empresa es el
// organismo central, como en la columna Contrato de la grilla.
export const empresaYEstablecimiento = (visita: any): string => {
    const establecimiento = visita?.establecimiento;
    const empresa =
        establecimiento?.organismo?.organismoCentral?.nombre ||
        establecimiento?.organismoCentral?.nombre ||
        establecimiento?.organismo?.nombre;
    const nombreEstablecimiento = establecimiento?.nombre || visita?.nombreEstablecimiento;

    return [empresa, nombreEstablecimiento].filter(Boolean).join(' — ');
};

// Mismo pedido del acta que el de la visita (handleDownloadActa de useVisitas),
// con el evento de la falla.
export const pedidoActaDeFalla = (visita: any, idEventoFalla: number) => ({
    idVisita: idDeLaVisita(visita),
    idTipoVisita: visita?.idTipoVisita ?? visita?.actividades?.[0]?.tipoVisita?.id,
    tipoActa: 1,
    idEventoFalla,
    datos: buildActaInterviewedData(visita)
});
